import { FC, useState } from 'react'
import { SxProps, Theme } from '@mui/material'
import { ConversationStatus, ConversationType } from 'models/conversations'

import { CloseConversationDialog } from './CloseConversationDialog'
import { GRAY_700 } from 'constants/styling/theme'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useTranslation } from 'react-i18next'

const DEFAULT_TRIGGER_SX: SxProps<Theme> = { flexShrink: 0, color: GRAY_700 }

interface Props {
  conversationId: string
  status: ConversationStatus
  conversationType?: ConversationType
  /** Styles applied to the ⋮ trigger button so each host (drawer header / reduced popover) can match its layout. */
  triggerSx?: SxProps<Theme>
  /** Called once the conversation is successfully closed — used by the reduced popover to leave the now-inactive thread. */
  onClosed?: () => void
}

/** ⋮ menu exposing the irreversible "Close conversation" action, gated behind a confirmation dialog. */
export const CloseConversationMenu: FC<Props> = ({ conversationId, status, conversationType, triggerSx = DEFAULT_TRIGGER_SX, onClosed }) => {
  const { t } = useTranslation(['chat'])

  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null)
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)

  const openCloseConfirm = () => {
    setActionsAnchor(null)
    setConfirmCloseOpen(true)
  }

  return (
    <>

      <IconButton
        size="small"
        aria-label={t('chat:header.actions')}
        aria-haspopup="menu"
        aria-expanded={Boolean(actionsAnchor)}
        onClick={(event) => setActionsAnchor(event.currentTarget)}
        sx={triggerSx}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={actionsAnchor}
        open={Boolean(actionsAnchor)}
        onClose={() => setActionsAnchor(null)}
      >
        <MenuItem
          disabled={status === ConversationStatus.CLOSED}
          onClick={openCloseConfirm}
        >
          {t('chat:header.close_conversation')}
        </MenuItem>
      </Menu>

      <CloseConversationDialog
        conversationId={conversationId}
        conversationType={conversationType}
        source="header_menu"
        isOpen={confirmCloseOpen}
        onCancel={() => setConfirmCloseOpen(false)}
        onClosed={() => {
          setConfirmCloseOpen(false)
          onClosed?.()
        }}
      />

    </>
  )
}
